import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { marked } from "marked";
// DB imports
import { db, auth } from "../../../firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
// Tiptap Library Imports
import { useEditor, EditorContent } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from '@tiptap/extension-text-style'
import { Color } from "@tiptap/extension-color";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";

// Other components imports
import CustomBubbleMenu from "./BubbleMenu";
import Highlight from '@tiptap/extension-highlight'; // <-- 1. IMPORT IT

import TiptapSideMenu from "../MaterialUI/TiptapSideMenu";
import "./SimpleTiptap.css";

const EditorsContainer = ({ content, id, geminiData }) => {
  return (
    <div>
      <div key={id}>
        <TiptapEditor
          initialContent={content}
          editorId={id}
          geminiData={geminiData} 
        />
      </div>
    </div>
  );
};

const TiptapEditor = React.memo(
  ({ editorId, initialContent, setTiptapContent, geminiData }) => {
    const containerRef = useRef();
    const [showDropdown, setShowDropdown] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const hoverTimeoutRef = useRef(null);
    const componentUID = uuidv4();
    const { viewId } = useParams();

    const handleMouseEnter = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 2000);
    };

    const editor = useEditor({
      extensions: [StarterKit, TextStyleKit, Color, TaskList, TaskItem, Highlight.configure({ multicolor: true })],
      content: initialContent,
      onUpdate: ({ editor }) => {
        const htmlContent = editor.getHTML();
        debouncedUpdateFirestore(htmlContent);
        setTiptapContent(htmlContent);
      },
    });

    // Ensure the content is set once on initial load
    useEffect(() => {
      if (editor && initialContent) {
        editor.commands.setContent(initialContent, false, { overwrite: true });
      }
    }, [editor, initialContent]);

    // Handle geminiData updates
    useEffect(() => {
      if (editor && geminiData) {
        const newContent = marked(geminiData.text);
        editor.commands.setContent(newContent, false, { overwrite: true });
        console.log("Editor content set with new Gemini data:", newContent);
      }
    }, [geminiData, editor]);

    const updateFirestore = useCallback(
      async (content) => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No authenticated user found.");
          return;
        }

        const docRef = doc(db, "views", viewId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          let newContents = docSnap.data().contents || [];
          const index = newContents.findIndex((c) => c.id === editorId);

          if (index >= 0) {
            newContents[index] = { ...newContents[index], content };
          } else {
            newContents.push({
              id: editorId,
              type: "text-editor",
              content,
              cards: 1,
            });
          }

          await updateDoc(docRef, { contents: newContents });
          console.log("Content updated successfully in Firestore.");
        }
      },
      [viewId, editorId]
    );

    const debouncedUpdateFirestore = useCallback(
      _.debounce(updateFirestore, 2000),
      [updateFirestore]
    );

    useEffect(() => {
      return () => editor?.destroy();
    }, [editor]);

    return (
      <div
        className="tiptap"
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: "relative" }}
      >
        {showDropdown && (
          <div
            className="dropdown-menu"
            style={{
              position: "absolute",
              top: "25px",
              left: "-140px",
              zIndex: 100,
              background: "#272A2C",
              padding: "16px",
              borderRadius: "8px",
              filter: "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))",
            }}
          >
            test
            <select
              onChange={(e) => {
                const level = parseInt(e.target.value, 10);
                editor.chain().focus().toggleHeading({ level }).run();
              }}
              value={
                editor.isActive("heading")
                  ? editor.getAttributes("heading").level
                  : ""
              }
            >
              <option value="">Normal Text</option>
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
            </select>
            <button
              onClick={() => editor.chain().focus().unsetAllMarks().run()}
            >
              Clear Marks
            </button>
            <button onClick={() => editor.chain().focus().clearNodes().run()}>
              Clear Nodes
            </button>
            <button
              onClick={() => {
                const content = editor.getJSON();
                editor.commands.setContent(content, true);
              }}
            >
              Duplicate
            </button>
            <button onClick={() => editor.commands.clearContent()}>
              Delete
            </button>
            <button
              onClick={() => {
                editor.chain().focus().toggleTaskList().run();
              }}
            >
              To-Do List
            </button>
          </div>
        )}
        {editor && <EditorContent editor={editor} />}
        {editor && <CustomBubbleMenu editor={editor} />}
        {isHovered && (
          <TiptapSideMenu
            editor={editor}
            style={{ position: "absolute", top: -8, left: -30 }}
          />
        )}
      </div>
    );
  }
);

export default EditorsContainer;